import { useAuthStore } from '@/core/stores/useAuthStore';
import { PROTOCOL_ERROR_CODE } from '@/core/types/error-codes';
import type { TokenRefreshRequest } from '@/generated/ts-rs/TokenRefreshRequest';
import type { TokenRefreshResponse } from '@/generated/ts-rs/TokenRefreshResponse';
import { API_BASE, ApiError, apiErrorFromResponse } from './client';

// Single shared refresh lock for the whole app, plus cross-tab serialisation
// via Web Locks (`navigator.locks`). Without these guards, the HTTP path
// (request() in client.ts), the WebSocket path (getValidWsToken in
// ws-token.ts), and any other tab can each fire concurrent /refresh calls
// with the same refresh_token. The server rotates the first one; everyone
// else's rotated-token request is rejected and falsely signs the user out.
let refreshPromise: Promise<string | null> | null = null;

const COOLDOWN_MS = 5000;
const LAST_REFRESH_KEY = 'redcoral-last-refresh-at';
const LOCK_NAME = 'redcoral-refresh';
const NETWORK_ERROR_CODE = 9003;

function readLastRefreshAt(): number {
  try {
    const raw = localStorage.getItem(LAST_REFRESH_KEY);
    return raw ? Number(raw) : 0;
  } catch {
    return 0;
  }
}

function writeLastRefreshAt(ts: number) {
  try {
    localStorage.setItem(LAST_REFRESH_KEY, String(ts));
  } catch { /* storage unavailable */ }
}

async function readRefreshSuccess(res: Response): Promise<TokenRefreshResponse> {
  let data: unknown;
  try {
    data = await res.json();
  } catch {
    throw new ApiError(res.status, 'Token refresh returned non-JSON success body', PROTOCOL_ERROR_CODE);
  }

  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    throw new ApiError(res.status, 'Token refresh returned malformed success body', PROTOCOL_ERROR_CODE);
  }

  const body = data as Record<string, unknown>;
  if (typeof body.token !== 'string' || body.token.length === 0) {
    throw new ApiError(res.status, 'Token refresh missing required string field: token', PROTOCOL_ERROR_CODE);
  }
  if (typeof body.refresh_token !== 'string' || body.refresh_token.length === 0) {
    throw new ApiError(res.status, 'Token refresh missing required string field: refresh_token', PROTOCOL_ERROR_CODE);
  }

  return { token: body.token, refresh_token: body.refresh_token };
}

function isPermanentRefreshRejection(error: ApiError): boolean {
  if (error.code === PROTOCOL_ERROR_CODE) return false;
  return error.status === 401 || error.status === 403 || error.status === 404;
}

/**
 * Attempt a token refresh. Returns the new access token on success, null on
 * permanent auth failure (session has been cleared; ProtectedRoute redirects).
 * Transient/protocol failures throw `ApiError` with a numeric code so callers
 * can surface the real failure instead of treating it as logout.
 *
 * Concurrent callers share the same in-flight refresh (in-tab promise lock)
 * AND a Web Locks-backed cross-tab lock (so two tabs never rotate the
 * refresh_token at once). A 5s cooldown timestamp lives in localStorage so
 * any tab can short-circuit if another just refreshed.
 */
export function refreshSession(): Promise<string | null> {
  // In-tab cooldown: if THIS tab already has a fresh token from a recent
  // refresh, skip straight back. Cross-tab cooldown is enforced again inside
  // the lock to handle the case where another tab refreshed concurrently.
  const currentToken = useAuthStore.getState().token;
  if (currentToken && Date.now() - readLastRefreshAt() < COOLDOWN_MS) {
    return Promise.resolve(currentToken);
  }
  if (!refreshPromise) {
    refreshPromise = doRefreshLocked().finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

/**
 * Wrap a raw fetch call with the same refresh-on-401 retry that `request()`
 * uses. Use for fetches that need binary/streaming response handling and
 * therefore can't go through `request()`.
 *
 * The caller passes a closure that builds a new Request given a token, so
 * the retry path can re-issue with the refreshed token.
 */
export async function authedFetch(
  token: string,
  doFetch: (token: string) => Promise<Response>,
): Promise<Response> {
  const res = await doFetch(token);
  if (res.status !== 401) return res;

  const newToken = await refreshSession();
  if (!newToken) return res;

  const retry = await doFetch(newToken);
  if (retry.status === 401) useAuthStore.getState().clearAuth();
  return retry;
}

async function doRefreshLocked(): Promise<string | null> {
  // navigator.locks serialises the refresh across all tabs in this origin.
  // The lock body re-checks the cross-tab cooldown so a tab that lost the
  // race for the lock can simply consume the winner's freshly-rotated token.
  const runner = async (): Promise<string | null> => {
    // Mirror the localStorage state into our in-memory store BEFORE deciding.
    // The winning tab wrote new tokens via updateTokens → localStorage; if
    // the storage event hasn't fired in this tab yet, rehydrate explicitly.
    useAuthStore.getState().rehydrate();
    const token = useAuthStore.getState().token;
    if (token && Date.now() - readLastRefreshAt() < COOLDOWN_MS) {
      return token;
    }
    return doRefresh();
  };

  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(LOCK_NAME, runner);
  }
  // Fallback for environments without Web Locks (older Safari, SSR).
  return runner();
}

async function doRefresh(): Promise<string | null> {
  const { refreshToken, updateTokens, clearAuth } = useAuthStore.getState();
  if (!refreshToken) {
    clearAuth();
    return null;
  }

  let res: Response;
  const body: TokenRefreshRequest = { refresh_token: refreshToken };
  try {
    res = await fetch(`${API_BASE}/api/tenant/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    // Network error (offline, CORS, server unreachable). Don't sign the user
    // out — the refresh_token is probably still valid.
    throw new ApiError(0, 'Token refresh network error', NETWORK_ERROR_CODE);
  }

  if (res.ok) {
    const data = await readRefreshSuccess(res);
    updateTokens(data.token, data.refresh_token);
    writeLastRefreshAt(Date.now());
    return data.token;
  }

  const error = await apiErrorFromResponse(res, 'Token refresh failed');
  if (isPermanentRefreshRejection(error)) {
    // Server-side auth rejection means the refresh_token/session is dead.
    // Sign out so ProtectedRoute redirects.
    clearAuth();
    return null;
  }
  throw error;
}
